import React from 'react';
import type { AxiosError } from 'axios';

import Button from '@hero/ui-kit/inputs/Button';
import P from '@hero/ui-kit/typography/P';
import Section from '@hero/ui-kit/layout/Section';

import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useDeviceErrorsReport from '../api/useDeviceErrorsReport';
import ClassificationsList from './ClassificationsList';
import SignalsBlock from './SignalsBlock';
import TopDispositionBadge from './TopDispositionBadge';
import UnavailableBanner from './UnavailableBanner';
import { getDispositionTagType } from './labels';
import * as styles from './style.module.css';

type Props = {
    externalSerial: string | null;
};

const formatTimestamp = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
};

const RECOMMENDATION_CLASS: Record<string, string> = {
    fail: styles.recommendationFail,
    warn: styles.recommendationWarn,
    success: styles.recommendationSuccess
};

const Diagnostics: React.FC<Props> = ({ externalSerial }) => {
    const { mutate, data, isPending, isError, error } = useDeviceErrorsReport();

    if (!externalSerial) {
        return null;
    }

    const handleFetch = () => mutate({ external_serial: externalSerial });
    const status = (error as AxiosError | null)?.response?.status;

    const hasReport =
        !isError &&
        data &&
        typeof data.top_disposition === 'string' &&
        data.top_disposition &&
        data.top_disposition !== 'unavailable';

    const recommendationClass = hasReport
        ? (RECOMMENDATION_CLASS[getDispositionTagType(data!.top_disposition)] ?? '')
        : '';

    // The classifications that drive the top recommendation — prefer the ones
    // whose disposition matches it, otherwise fall back to everything fired.
    const drivingReasons = hasReport
        ? (() => {
              const all = Array.isArray(data!.classifications) ? data!.classifications : [];
              const matching = all.filter((c) => c?.disposition === data!.top_disposition);
              return (matching.length > 0 ? matching : all).map((c) => ({
                  pattern: c?.pattern ?? 'unknown',
                  confidence: c?.confidence
              }));
          })()
        : [];

    return (
        <div className={styles.panel}>
            {!data && !isPending && !isError ? (
                <Section padding="small" centered>
                    <Button size="regular" width="large" onClick={handleFetch}>
                        Show diagnostics
                    </Button>
                </Section>
            ) : null}

            {isPending ? <InlineLoader /> : null}

            {isError ? (
                status === 429 ? (
                    <>
                        <P>Diagnostics is rate-limited. Please wait a moment before retrying.</P>
                        <Button onClick={handleFetch}>Retry</Button>
                    </>
                ) : (
                    <>
                        <QueryInlineError error={error} />
                        <Button onClick={handleFetch}>Retry</Button>
                    </>
                )
            ) : null}

            {!isError && data && data.top_disposition === 'unavailable' ? (
                <UnavailableBanner reason={data.unavailable_reason ?? null} onRetry={handleFetch} />
            ) : null}

            {hasReport ? (
                <>
                    <div className={`${styles.recommendation} ${recommendationClass}`}>
                        <TopDispositionBadge
                            disposition={data!.top_disposition}
                            reasons={drivingReasons}
                        />
                        <div className={styles.meta}>
                            <span>
                                Generated {formatTimestamp(data!.generated_at)} · cached up to 1h
                            </span>
                            {data!.signals?.date_range ? (
                                <span>
                                    {`Data window ${formatTimestamp(
                                        data!.signals.date_range.from
                                    )} → ${formatTimestamp(data!.signals.date_range.to)}`}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <ClassificationsList classifications={data!.classifications} />
                    {data!.signals ? <SignalsBlock signals={data!.signals} /> : null}

                    <Button onClick={handleFetch} className={styles.refresh}>
                        Refresh
                    </Button>
                </>
            ) : null}
        </div>
    );
};

export default Diagnostics;
